import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatValue, formatCompactValue, toneColor } from "../../lib/kernelFormat.js";
import { useLabels, scopeStepLabel } from "../../lib/dimensionDefs.js";
import { sanitizeLocalDownloadHref } from "../../lib/renderSafety.js";
import { SurfaceChart } from "../ChartBlocks.jsx";
import ScrollArea from "../ScrollArea.jsx";
import OverflowTooltip from "../OverflowTooltip.jsx";
import { KernelEmpty } from "./shared.jsx";
import {
    gridTemplate,
    totalMinWidth,
    buildCsv,
    mergeTableRows,
    sortRows,
} from "./tableLayout.js";
import { loadKernelTableDetail } from "../../lib/kernelTableDetail.js";

export const kind = "table";

// Horizontal overlay scrollbar (OverlayScrollbars via ScrollArea) — same config
// as TabStrip/QueryBar. Native scrollbars are avoided app-wide: they steal
// layout height and conflict with the OverlayScrollbars-managed ancestors.
const SCROLL_X_OPTIONS = Object.freeze({
    overflow: { x: "scroll", y: "hidden" },
    scrollbars: { autoHide: "leave", autoHideDelay: 200 },
});

// One glyph for every affordance in this table -- the row disclosure and the
// header sort indicator both -- rotated rather than swapped. It was a literal
// `v` and `>`, which read as text sitting in the cell rather than as a control.
// Drawn rather than typed: a character would have to be either an ASCII letter
// or a Unicode arrow, and this is markup, so it is neither.
function Chevron({ turn = 0, size = 10 }) {
    return (
        <svg
            aria-hidden
            viewBox="0 0 16 16"
            width={size}
            height={size}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 transition-transform duration-150 motion-reduce:transition-none"
            style={{ transform: `rotate(${turn}deg)` }}
        >
            <path d="M4 6l4 4 4-4" />
        </svg>
    );
}

export function Component({ spec, data, onOpenScope, loadDetail = loadKernelTableDetail }) {
    // Opt-in row expansion. A table only becomes expandable when its spec names
    // a detail field AND a row actually carries detail rows there, so every
    // existing table renders byte-identically -- the two built-ins on this
    // primitive (new-models, token-by-model) and every user-authored one.
    const detailField = typeof spec.detailField === "string" ? spec.detailField : null;
    const detailColumns = Array.isArray(spec.detailColumns) ? spec.detailColumns : [];
    const detailQuery = spec.detailQuery?.spec && typeof spec.detailQuery.spec === "object"
        ? spec.detailQuery
        : null;
    const [expanded, setExpanded] = useState(() => new Set());
    const [lazyDetails, setLazyDetails] = useState(() => new Map());
    const detailControllers = useRef(new Map());

    useEffect(() => {
        // Parent results can change without remounting the primitive. Never
        // carry an expanded row or its endpoint-scoped detail into the next
        // result set (including across tenant/scope changes).
        setExpanded(new Set());
        setLazyDetails(new Map());
        return () => {
            for (const controller of detailControllers.current.values()) controller.abort();
            detailControllers.current.clear();
        };
    }, [data, spec.merge, detailQuery]);

    const loadLazyDetail = useCallback(async (key, row) => {
        const current = detailControllers.current.get(key);
        current?.abort();
        const controller = new AbortController();
        detailControllers.current.set(key, controller);
        setLazyDetails((prev) => new Map(prev).set(key, { status: "loading", rows: [] }));
        try {
            const rows = await loadDetail(detailQuery, row, { signal: controller.signal });
            if (controller.signal.aborted) return;
            setLazyDetails((prev) => new Map(prev).set(key, {
                status: "done",
                rows: Array.isArray(rows) ? rows : [],
            }));
        } catch (error) {
            if (controller.signal.aborted || error?.name === "AbortError") return;
            setLazyDetails((prev) => new Map(prev).set(key, {
                status: "error",
                rows: [],
                error,
            }));
        } finally {
            if (detailControllers.current.get(key) === controller) {
                detailControllers.current.delete(key);
            }
        }
    }, [detailQuery, loadDetail]);

    const toggle = useCallback((key, row, isOpen) => {
        if (!detailQuery) {
            setExpanded((prev) => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                return next;
            });
            return;
        }

        // Lazy detail is single-flight per table. Opening another row closes
        // and aborts the prior one, bounding expansion-driven query concurrency
        // at one even if a reader clicks rapidly through the fleet list.
        for (const [otherKey, controller] of detailControllers.current) {
            if (otherKey !== key || isOpen) {
                controller.abort();
                detailControllers.current.delete(otherKey);
            }
        }
        setExpanded(isOpen ? new Set() : new Set([key]));
        if (isOpen) return;
        const cached = lazyDetails.get(key);
        if (cached?.status !== "done") void loadLazyDetail(key, row);
    }, [detailQuery, lazyDetails, loadLazyDetail]);

    const rawData = useMemo(
        () => mergeTableRows(Array.isArray(data) ? data : [], spec.merge),
        [data, spec.merge],
    );

    const columns = Array.isArray(spec.columns) ? spec.columns : [];

    // The initial order the reader sees. Absent a declared default, rows keep
    // the order the query returned them in -- which for a server-ordered table
    // is already the intended one.
    const defaultSort = useMemo(() => {
        const declared = spec.defaultSort && typeof spec.defaultSort === "object" ? spec.defaultSort : null;
        if (declared?.id) return { id: declared.id, dir: declared.dir === "asc" ? "asc" : "desc" };
        return null;
    }, [spec.defaultSort]);
    const [chosenSort, setChosenSort] = useState(null);
    const sort = chosenSort || defaultSort;

    const sortBy = useCallback((id) => {
        setChosenSort((prev) => {
            const current = prev || defaultSort;
            if (current?.id === id) return { id, dir: current.dir === "asc" ? "desc" : "asc" };
            // A first click on a new column opens on its most useful end: largest
            // or most recent for quantities and times, A-Z for names.
            const column = columns.find((c) => c.id === id);
            const textual = !column?.format || column.format === "text";
            return { id, dir: textual ? "asc" : "desc" };
        });
    }, [columns, defaultSort]);

    // A single detail-sort applied to every open detail sub-table, client-side
    // over the rows already fetched for that parent. Mirrors the parent header
    // sort. CAVEAT: for a kernel whose detail is a globally-capped EAGER query
    // (local-ai-detail's top-N breakdown), this reorders the loaded slice, which
    // at fleet scale can be a truncated view of one parent's rows -- it sorts
    // what is shown, not a re-query. Kernels with a bounded per-parent detail
    // (the lazy `detailQuery` path, e.g. local-ai-runtime-exec) fetch the
    // complete set for the opened row, so their detail sort is exact.
    const [detailSort, setDetailSort] = useState(null);
    const sortDetailBy = useCallback((id) => {
        setDetailSort((prev) => {
            if (prev?.id === id) return { id, dir: prev.dir === "asc" ? "desc" : "asc" };
            const column = detailColumns.find((c) => c.id === id);
            const textual = !column?.format || column.format === "text";
            return { id, dir: textual ? "asc" : "desc" };
        });
    }, [detailColumns]);

    // Source positions identify occurrences, even when values or row objects
    // repeat. Carry them through sorting so React and detail state agree.
    const keyedData = useMemo(() => rawData.map((row, key) => ({ row, key })), [rawData]);
    const sortedEntries = useMemo(
        () => sortRows(keyedData, sort, (entry) => entry.row),
        [keyedData, sort],
    );
    const sortedData = useMemo(() => sortedEntries.map(({ row }) => row), [sortedEntries]);
    const visibleEntries = useMemo(() => {
        const cap = spec.maxRows || 25;
        return sortedEntries.slice(0, cap);
    }, [sortedEntries, spec.maxRows]);
    const rows = useMemo(() => visibleEntries.map(({ row }) => row), [visibleEntries]);

    // Build the step list for useLabels so cells with link.dim get
    // resolved against the same labelCache the dashboard uses
    // everywhere else — UUIDs render as hostnames as the cache fills.
    const labelSteps = useMemo(() => {
        if (!rows.length || !columns.length) return [];
        const seen = new Set();
        const steps = [];
        for (const col of columns) {
            // `dim` resolves labels; `link` additionally makes the cell click
            // through. A column can want the hostname without the navigation.
            const dim = col?.link?.dim || col?.dim;
            if (!dim) continue;
            const idField = col?.link?.idField || col.id;
            for (const row of rows) {
                const id = row?.[idField];
                if (id == null) continue;
                const key = `${dim}:${id}`;
                if (seen.has(key)) continue;
                seen.add(key);
                steps.push({ dim, id: String(id) });
            }
        }
        return steps;
    }, [columns, rows]);
    useLabels(labelSteps);

    const template = useMemo(() => gridTemplate(columns), [columns]);
    const detailTemplate = useMemo(() => gridTemplate(detailColumns), [detailColumns]);
    const minWidth = useMemo(() => totalMinWidth(columns), [columns]);

    if (!rows.length || !columns.length) {
        return <KernelEmpty message={spec.empty || "no rows"} />;
    }

    return (
        <div className="overflow-hidden rounded-[2px] border border-[color:var(--line)]">
            {/* Single horizontal-scroll container via OverlayScrollbars (ScrollArea),
                NOT a native overflow-x — native scrollbars steal layout height and
                conflict with the OverlayScrollbars-managed ancestors. The minWidth
                floor keeps every column legible and forces the overflow; narrow
                tables still fill the width via the 1fr tracks. Header and rows share
                this one scroller so they stay aligned. data-export-full-width lets
                PNG export widen this (and its OverlayScrollbars viewport) to full
                content. */}
            <ScrollArea className="min-w-0" options={SCROLL_X_OPTIONS} data-export-full-width>
                <div style={{ minWidth: `${minWidth}px` }}>
                    <div
                        className="grid bg-[color:var(--panel-soft)] text-[10.5px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]"
                        style={{ gridTemplateColumns: template }}
                    >
                        {columns.map((c, ci) => {
                            const label = c.label || c.id;
                            // `uppercase` on the cell, not only the row: a sortable
                            // header is a <button>, and the CSS reset gives buttons
                            // text-transform: none, so the row's casing stopped there.
                            const cell = `px-[10px] py-[5px] border-b border-[color:var(--line)] uppercase ${c.align === "right" ? "text-right" : "text-left"}`;
                            // A sparkline column holds a per-row series, which has
                            // no order to sort on. A malformed custom column with
                            // no id has no cell value to sort on either.
                            if (c.spark || !c.id) {
                                // OverflowTooltip is the dashboard's truncate-aware
                                // hover tooltip (instant + styled, portaled to
                                // <body>) the data tables use. It applies `truncate`
                                // itself and reveals the full label on hover only
                                // when it actually overflows.
                                return <OverflowTooltip key={c.id || `column-${ci}`} as="div" text={label} className={cell} />;
                            }
                            const active = sort?.id === c.id;
                            return (
                                <OverflowTooltip
                                    key={c.id}
                                    as="button"
                                    type="button"
                                    onClick={() => sortBy(c.id)}
                                    // The header is a plain grid cell, not a real
                                    // `columnheader`, so `aria-sort` would have no
                                    // role to attach to. The state rides the
                                    // accessible name instead, where a screen reader
                                    // reads it without inventing table semantics the
                                    // rest of this primitive does not have.
                                    ariaLabel={active
                                        ? `${label}, sorted ${sort.dir === "asc" ? "ascending" : "descending"}. Activate to reverse.`
                                        : `${label}, not sorted. Activate to sort.`}
                                    // Label as the tooltip, label+arrow as the
                                    // content: the hover text should read the
                                    // column name, not the sort glyph.
                                    text={label}
                                    className={`${cell} w-full cursor-pointer hover:text-[color:var(--fg)] ${active ? "text-[color:var(--fg)]" : ""}`}
                                >
                                    <span className={`inline-flex max-w-full items-center gap-[4px] ${c.align === "right" ? "justify-end" : ""}`}>
                                        <span className="truncate">{label}</span>
                                        {/* The slot is always present, so sorting a
                                            column cannot shift the header labels. */}
                                        <span className={active ? "text-[color:var(--fg)]" : "opacity-0"}>
                                            <Chevron turn={active && sort.dir === "asc" ? 180 : 0} size={9} />
                                        </span>
                                    </span>
                                </OverflowTooltip>
                            );
                        })}
                    </div>
                    {visibleEntries.map(({ row, key: rowKey }, ri) => {
                        const eagerDetail = detailField && Array.isArray(row?.[detailField])
                            ? row[detailField]
                            : null;
                        const lazyDetail = detailQuery ? lazyDetails.get(rowKey) : null;
                        const detail = detailQuery ? lazyDetail?.rows ?? null : eagerDetail;
                        const canExpand = Boolean(detail?.length || detailQuery);
                        const isOpen = expanded.has(rowKey);
                        // The table's last visible element carries no bottom
                        // rule -- the panel edge already draws one, and a
                        // second reads as an empty row. Wrapping each row in a
                        // div for the detail panel put every row last inside
                        // its own wrapper, so this cannot be a `last:` variant;
                        // whether an open panel follows decides which of the
                        // two elements is the last one.
                        const isLastRow = ri === rows.length - 1;
                        const panelOpen = Boolean(isOpen && canExpand);
                        const rule = "border-b border-[color:var(--line-soft)]";
                        return (
                            <div key={rowKey}>
                                <div
                                    className={`grid ${isLastRow && !panelOpen ? "" : rule} text-[12px] leading-[1.35] text-[color:var(--muted)] ${canExpand ? "cursor-pointer hover:bg-[color:var(--panel-soft)]" : ""}`}
                                    style={{ gridTemplateColumns: template }}
                                    data-table-row="true"
                                    // The whole row is a pointer target, because a
                                    // caret is a small one in a dense table and
                                    // every cell describes the machine the
                                    // disclosure is about. It carries no role or
                                    // tab stop: the caret button below is the
                                    // accessible toggle, and duplicating it here
                                    // would nest one control inside another and
                                    // fire both on a keyboard Enter.
                                    onClick={canExpand ? () => toggle(rowKey, row, isOpen) : undefined}
                                    data-expandable={canExpand ? "true" : undefined}
                                >
                                    {columns.map((c, ci) => (
                                        c.spark
                                            ? <SparkCell key={c.id} column={c} row={row} />
                                            : (
                                                <Cell
                                                    key={c.id}
                                                    column={c}
                                                    row={row}
                                                    onOpenScope={onOpenScope}
                                                    caret={ci === 0 && canExpand ? <Chevron turn={isOpen ? 0 : -90} size={11} /> : null}
                                                    expanded={isOpen}
                                                    onToggle={() => toggle(rowKey, row, isOpen)}
                                                    inExpandableRow={canExpand}
                                                />
                                            )
                                    ))}
                                </div>
                                {panelOpen ? (
                                    <div
                                        className={`${isLastRow ? "" : rule} bg-[color:var(--panel-soft)] pl-[18px]`}
                                        aria-busy={lazyDetail?.status === "loading" ? "true" : undefined}
                                    >
                                        {lazyDetail?.status === "loading" ? (
                                            <div className="px-[10px] py-[8px] text-[11px] text-[color:var(--muted)]">Loading details...</div>
                                        ) : lazyDetail?.status === "error" ? (
                                            <div className="px-[10px] py-[8px] text-[11px] text-[color:var(--muted)]">Could not load details. Collapse and open the row to retry.</div>
                                        ) : detail?.length ? (
                                            <>
                                                <div
                                                    className="grid text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]"
                                                    style={{ gridTemplateColumns: detailTemplate }}
                                                >
                                                    {detailColumns.map((c) => {
                                                        const dLabel = c.label || c.id;
                                                        const dCell = `px-[10px] py-[4px] ${c.align === "right" ? "text-right" : "text-left"}`;
                                                        if (c.spark) {
                                                            return <OverflowTooltip key={c.id} as="div" text={dLabel} className={dCell} />;
                                                        }
                                                        const dActive = detailSort?.id === c.id;
                                                        return (
                                                            <OverflowTooltip
                                                                key={c.id}
                                                                as="button"
                                                                type="button"
                                                                onClick={() => sortDetailBy(c.id)}
                                                                ariaLabel={dActive
                                                                    ? `${dLabel}, sorted ${detailSort.dir === "asc" ? "ascending" : "descending"}. Activate to reverse.`
                                                                    : `${dLabel}, not sorted. Activate to sort.`}
                                                                text={dLabel}
                                                                className={`${dCell} w-full cursor-pointer hover:text-[color:var(--fg)] ${dActive ? "text-[color:var(--fg)]" : ""}`}
                                                            >
                                                                <span className={`inline-flex max-w-full items-center gap-[4px] ${c.align === "right" ? "justify-end" : ""}`}>
                                                                    <span className="truncate">{dLabel}</span>
                                                                    <span className={dActive ? "text-[color:var(--fg)]" : "opacity-0"}>
                                                                        <Chevron turn={dActive && detailSort.dir === "asc" ? 180 : 0} size={9} />
                                                                    </span>
                                                                </span>
                                                            </OverflowTooltip>
                                                        );
                                                    })}
                                                </div>
                                                {sortRows(detail, detailSort).map((child, di) => (
                                                    <div
                                                        key={di}
                                                        className="grid text-[11px] leading-[1.3] text-[color:var(--muted)]"
                                                        style={{ gridTemplateColumns: detailTemplate }}
                                                    >
                                                        {detailColumns.map((c) => (
                                                            <Cell key={c.id} column={c} row={child} onOpenScope={onOpenScope} />
                                                        ))}
                                                    </div>
                                                ))}
                                            </>
                                        ) : (
                                            <div className="px-[10px] py-[8px] text-[11px] text-[color:var(--muted)]">No details in range.</div>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            </ScrollArea>
            {/* Escape hatch for dense/wide tables: download the FULL dataset (not
                the maxRows-capped render) as CSV. data-export-ignore keeps it out
                of PNG exports. */}
            <div
                data-export-ignore="true"
                className="flex items-center justify-between border-t border-[color:var(--line-soft)] bg-[color:var(--panel-soft)] px-[8px] py-[3px]"
            >
                <span className="text-[11px] text-[color:var(--muted)]">
                    {rawData.length > rows.length ? `${rows.length} of ${rawData.length} rows` : `${rawData.length} rows`}
                </span>
                <button
                    type="button"
                    onClick={() => downloadCsv(columns, sortedData, `table-${Date.now()}.csv`)}
                    className="cursor-pointer rounded-[5px] border border-[color:var(--line)] px-[6px] py-[1px] text-[11px] text-[color:var(--muted)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]"
                >
                    ↓ CSV
                </button>
            </div>
        </div>
    );
}

function downloadCsv(columns, rows, filename) {
    const blob = new Blob([buildCsv(columns, rows)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const href = sanitizeLocalDownloadHref(url);
    if (!href) {
        URL.revokeObjectURL(url);
        return;
    }
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

// Inline mini line-chart cell. The cell value (row[spark.field] or row[id]) is
// an array of numbers — one point per bucket (e.g. a per-skill daily trend).
function SparkCell({ column, row }) {
    const arr = row?.[column.spark?.field || column.id];
    const series = Array.isArray(arr) ? arr.map((n) => Number(n) || 0) : [];
    if (!series.length) {
        return <div className="px-[10px] py-[5px] text-[color:var(--muted-soft)]">—</div>;
    }
    return (
        <div className="px-[8px] py-[4px]">
            <SurfaceChart
                mode="line"
                series={series}
                stroke={column.spark?.color || "var(--ink)"}
                fill={column.spark?.color || "var(--ink)"}
                className="relative w-full text-[color:var(--plot-ink-soft)]"
                padding={2}
                width={120}
                height={22}
                strokeWidth={1.2}
                haloWidth={2.5}
                haloStroke="var(--bg)"
            />
        </div>
    );
}

function Cell({ column, row, onOpenScope, caret = null, expanded = false, onToggle = null, inExpandableRow = false }) {
    const raw = row?.[column.id];
    const link = column.link;
    const id = link?.idField ? row?.[link.idField] : ((link || column.dim) ? raw : null);
    // Numeric columns render a scale-aware compact form (8.8M) so wide
    // counts don't truncate mid-number in a narrow column; the exact value
    // stays reachable on hover (always-tooltip) whenever it was abbreviated.
    const numericCompact = !link && (column.format === "number" || column.format === "integer");
    const exactText = formatValue(raw, column.format);
    const labelDim = link?.dim || column.dim;
    const explicitLabel = link?.labelField ? row?.[link.labelField] : null;
    const resolvedLabel = labelDim && id != null
        ? scopeStepLabel({ dim: labelDim, id: String(id) })
        : null;
    let unresolved = Boolean(
        labelDim
        && id != null
        && (explicitLabel == null || explicitLabel === "")
        && String(resolvedLabel) === String(id),
    );
    let display = explicitLabel != null && explicitLabel !== ""
        ? formatValue(explicitLabel, column.format)
        : labelDim && id != null
            ? resolvedLabel
            : (numericCompact ? formatCompactValue(raw, column.format) : exactText);
    const abbreviated = numericCompact && typeof display === "string" && display !== exactText;
    // A column may declare `emptyText` to show a placeholder (e.g. "N/A") when
    // the row has no value, rather than rendering a blank cell.
    if ((raw == null || raw === "") && typeof column.emptyText === "string") {
        display = column.emptyText;
        unresolved = false;
    } else if (unresolved && typeof column.unresolvedText === "string") {
        display = column.unresolvedText;
    }
    // A linked cell inside an expandable row is two targets, not two actions on
    // one: the link stops the event so clicking the name navigates WITHOUT also
    // toggling the row underneath it. Without that a reader who clicks the
    // hostname gets both, and cannot tell which they asked for.
    const handler = id != null && link && onOpenScope ? (event) => {
        event?.stopPropagation?.();
        onOpenScope({
            dim: link.dim,
            ids: [String(id)],
            label: typeof display === "string" ? display : String(id),
        });
    } : null;
    const textColor = column.tone ? toneColor(column.tone) : undefined;
    const align = column.align === "right" ? "text-right" : "text-left";
    const isNumeric = !link && (column.format === "number" || column.format === "integer"
        || column.format === "percent" || column.format === "0.00" || column.format === "0.0");
    const tipText = unresolved && typeof column.unresolvedText === "string"
        ? display
        : labelDim
        ? `${display} · ${raw ?? ""}`
        : (abbreviated ? exactText : (typeof display === "string" ? display : String(raw ?? "")));
    // The OverflowTooltip Tag IS the cell element (button when linked,
    // else div) — never a wrapper around an inline-block child — so
    // `truncate`'s ellipsis paints on the same node that's measured for
    // overflow and shows the tooltip. The narrowly typed text color + click
    // ride on that same node without forwarding an opaque DOM prop bag.
    const base = `px-[10px] py-[5px] ${align} ${isNumeric ? "font-mono tabular-nums" : ""}`;
    // With a caret this cell becomes a two-child row, so the horizontal padding
    // moves to the wrapper and the content keeps only its vertical rhythm.
    const inner = caret ? `py-[5px] ${align} ${isNumeric ? "font-mono tabular-nums" : ""}` : base;
    const content = handler ? (
        <OverflowTooltip
            as="button"
            type="button"
            onClick={handler}
            ariaLabel={`Open ${display} in ${link.dim}`}
            textColor={textColor}
            text={tipText}
            always={abbreviated}
            // Inside an expandable row the link shrinks to its text: at
            // full column width the empty remainder of the cell navigates
            // too, so aiming anywhere right of the hostname opened Explore
            // when the reader meant to expand the row.
            className={`${inner} ${inExpandableRow ? "w-fit justify-self-start" : "w-full"} cursor-pointer underline decoration-dotted underline-offset-2 hover:decoration-solid`}
        >
            {display}
        </OverflowTooltip>
    ) : (
        <OverflowTooltip as="div" textColor={textColor} text={tipText} always={abbreviated} className={inner}>
            {display}
        </OverflowTooltip>
    );
    if (!caret) return content;
    // The caret is the row's accessible toggle and sits BESIDE the link rather
    // than inside it: a control nested in another control is not reliably
    // reachable, and the row itself deliberately carries no keyboard handler.
    const name = typeof display === "string" ? display : String(id ?? raw ?? "");
    return (
        <div className={`flex min-w-0 items-center gap-[6px] pl-[10px] ${align === "text-right" ? "justify-end" : ""}`}>
            <button
                type="button"
                onClick={(event) => { event.stopPropagation(); onToggle?.(); }}
                aria-expanded={expanded}
                aria-label={`${expanded ? "Collapse" : "Expand"} details for ${name}`}
                className="leading-none text-[color:var(--muted-soft)] hover:text-[color:var(--fg)]"
            >
                {caret}
            </button>
            {content}
        </div>
    );
}
